import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useLocaleFmt } from "@/lib/i18n/format";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { adminListUsers, adminSetPlan, adminSetAiLimit, adminResetAiUsage } from "@/lib/admin.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/subscriptions")({
  beforeLoad: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw redirect({ to: "/login" });
    const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", user.id);
    if (!roles?.some((r) => r.role === "admin")) throw redirect({ to: "/app" });
  },
  component: AdminSubsPage,
});

function AdminSubsPage() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const [users, setUsers] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = () => adminListUsers().then(setUsers);
  useEffect(() => { load(); }, []);

  const setPlan = async (id: string, plan: string) => {
    setBusy(id);
    const r = await adminSetPlan({ data: { userId: id, plan: plan as any } });
    if (!r.ok) toast.error(r.error); else toast.success(t("pages.admin.subs.planUpdated"));
    setBusy(null); load();
  };
  const setLimit = async (id: string, limit: number) => {
    setBusy(id);
    const r = await adminSetAiLimit({ data: { userId: id, limit } });
    if (!r.ok) toast.error(r.error); else toast.success(t("pages.admin.subs.limitUpdated"));
    setBusy(null); load();
  };
  const reset = async (id: string) => {
    setBusy(id);
    const r = await adminResetAiUsage({ data: { userId: id } });
    if (!r.ok) toast.error(r.error); else toast.success(t("pages.admin.subs.usageReset"));
    setBusy(null); load();
  };

  return (
    <>
      <PageHeader title={t("pages.admin.subs.title")} description={t("pages.admin.subs.description")} />
      <PageBody>
        {!users ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t("pages.admin.cols.user")}</th>
                  <th className="px-3 py-2">{t("pages.admin.cols.plan")}</th>
                  <th className="px-3 py-2">{t("pages.admin.cols.aiQuota")}</th>
                  <th className="px-3 py-2">{t("pages.admin.cols.limit")}</th>
                  <th className="px-3 py-2">{t("pages.admin.cols.lastPayment")}</th>
                  <th className="px-3 py-2">{t("pages.admin.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t">
                    <td className="px-3 py-2">
                      <div className="font-medium">{u.email}</div>
                      <div className="text-xs text-muted-foreground">{u.first_name} {u.last_name} {u.roles?.includes("admin") && <Badge variant="secondary" className="ml-1">admin</Badge>}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Select value={u.plan} onValueChange={(v) => setPlan(u.id, v)}>
                        <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="starter">Starter</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                          <SelectItem value="studio">Studio</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{u.ai_queries_used} / {u.ai_queries_limit}</td>
                    <td className="px-3 py-2">
                      <Input type="number" defaultValue={u.ai_queries_limit} className="h-8 w-24"
                        onBlur={(e) => { const v = parseInt(e.target.value); if (v !== u.ai_queries_limit) setLimit(u.id, v); }} />
                    </td>
                    <td className="px-3 py-2 text-xs">{u.last_payment ? new Date(u.last_payment.occurred_at).toLocaleDateString(localeTag) : "-"}</td>
                    <td className="px-3 py-2">
                      <Button size="sm" variant="ghost" onClick={() => reset(u.id)} disabled={busy === u.id}>{t("pages.admin.subs.resetQuota")}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </PageBody>
    </>
  );
}
